import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Group,
  MedicalServices,
  MonetizationOn,
  School,
  Warning,
  WhatsApp,
} from "@mui/icons-material";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CompanyDashboard as CompanyDashboardType,
  getCompanyDashboard,
  getTrainingComplianceColor,
} from "src/apis/empresa";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { getUserCompanyId } from "src/utils/auth";
import { formatCurrency } from "src/utils/currency";
import { getFirstDayOfMonth, getLastDayOfMonth } from "src/utils/date";

const WHATSAPP_NR1_URL =
  "https://wa.me/5519998449211?text=Ol%C3%A1%2C%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20a%20avalia%C3%A7%C3%A3o%20psicossocial%20e%20conformidade%20com%20a%20NR-1.";

const trainingBorderColor: Record<string, string> = {
  vermelho: "#d32f2f",
  amarelo:  "#f9a825",
  verde:    "#2e7d32",
};

const CompanyDashboard: FC = () => {
  const [dashboard, setDashboard] = useState<CompanyDashboardType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const companyId = getUserCompanyId();
  const navigate = useNavigate();

  useEffect(() => {
    if (!companyId) return;

    const fetchDashboard = async () => {
      setIsLoading(true);
      setError("");
      try {
        const data = await getCompanyDashboard(companyId, {
          data_inicio: getFirstDayOfMonth(),
          data_fim: getLastDayOfMonth(),
        });
        setDashboard(data);
      } catch (err: any) {
        setError(err?.message || "Não foi possível carregar o dashboard.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [companyId]);

  if (isLoading) {
    return <SkeletonLoad variant="cards" rows={4} />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!dashboard) return null;

  const treinamentosColor =
    trainingBorderColor[dashboard.treinamentos?.status_cor?.toLowerCase()] ?? "#607d8b";
  const treinamentosChipColor = getTrainingComplianceColor(dashboard.treinamentos?.status_cor);

  const rf = dashboard.resumo_financeiro;

  return (
    <Box display="flex" flexDirection="column" gap={3} mt={2}>
      <Typography variant="h6">
        Dashboard — {dashboard.empresa}
      </Typography>

      <Grid container spacing={2}>
        {/* Atendimentos totais */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={1} sx={{ height: "100%", padding: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <MedicalServices color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Atendimentos totais no mês
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {dashboard.atendimentos_totais_mes ?? 0}
            </Typography>
          </Paper>
        </Grid>

        {/* Colaboradores ativos */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={1} sx={{ height: "100%", padding: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Group color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Colaboradores ativos no mês
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {dashboard.colaboradores_ativos_mes ?? 0}{" "}
              <Typography component="span" variant="body1" color="text.secondary">
                de {dashboard.colaboradores_total ?? 0}
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{dashboard.percentual_colaboradores_ativos_mes ?? 0}%</strong>{" "}
              dos colaboradores utilizaram a EezyCare este mês
            </Typography>
          </Paper>
        </Grid>

        {/* Treinamentos */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={1}
            sx={{
              height: "100%",
              padding: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              border: `2px solid ${treinamentosColor}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <School fontSize="small" sx={{ color: treinamentosColor }} />
              <Typography variant="body2" color="text.secondary">
                Treinamentos
              </Typography>
              <Chip
                label={`${dashboard.treinamentos?.percentual_conformidade ?? 0}%`}
                color={treinamentosChipColor === 'default' ? undefined : treinamentosChipColor}
                size="small"
              />
            </Box>
            <Typography variant="body2">
              <strong>{dashboard.treinamentos?.total_realizados_empresa ?? 0}</strong> de{" "}
              {dashboard.treinamentos?.total_disponiveis ?? 0} realizados
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate("/empresa/minha")}
              sx={{ alignSelf: "flex-start" }}
            >
              Ver checklist de treinamentos
            </Button>
          </Paper>
        </Grid>

        {/* NR-1 */}
        <Grid item xs={12} sm={6} md={6}>
          <Paper elevation={1} sx={{ height: "100%", padding: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              {dashboard.nr1?.em_conformidade ? (
                <CheckCircle color="success" fontSize="small" />
              ) : (
                <Warning color="error" fontSize="small" />
              )}
              <Typography variant="body2" color="text.secondary">
                NR-1
              </Typography>
            </Box>

            {dashboard.nr1?.em_conformidade ? (
              <Alert severity="success" icon={false}>
                Empresa em conformidade com a NR-1
              </Alert>
            ) : (
              <>
                <Alert severity="error" icon={false}>
                  Empresa fora de conformidade com a NR-1
                </Alert>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<WhatsApp />}
                  href={WHATSAPP_NR1_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{ alignSelf: "flex-start" }}
                >
                  Solicitar avaliação psicossocial
                </Button>
              </>
            )}
          </Paper>
        </Grid>

        {/* Resumo financeiro */}
        <Grid item xs={12} sm={12} md={6}>
          <Paper elevation={1} sx={{ height: "100%", padding: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <MonetizationOn color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Resumo financeiro do mês
              </Typography>
              <Chip
                label={rf?.mensalidade_quitada ? "Quitada" : "Pendente"}
                color={rf?.mensalidade_quitada ? "success" : "warning"}
                size="small"
              />
            </Box>

            <Box display="flex" gap={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Valor pendente
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(rf?.valor_mensal_pendente)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Valor consolidado
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(rf?.valor_mensal_consolidado)}
                </Typography>
              </Box>
            </Box>

            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate("/empresa/resumo-financeiro")}
              sx={{ alignSelf: "flex-start" }}
            >
              Ver resumo financeiro completo
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyDashboard;
